import { queryOptions, useQuery } from '@tanstack/react-query';
import { apiClient } from '../client';
import { externalContactKeys } from '../query-keys';
import { LOOKUP_LIST_CACHE } from '@/lib/query-client';
import { useAuthStore } from '@/stores/auth-store';
import {
  listExternalContactsContract,
  showExternalContactContract,
  type ExternalContact,
  type ListExternalContactsResponse,
  type ShowExternalContactResponse
} from '@bliks-io/contracts';

/**
 * Fetch external contacts list
 *
 * @example
 * ```tsx
 * function ExternalContactsList() {
 *   const { data: contacts, isLoading } = useExternalContacts();
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!contacts) return null;
 *
 *   return <List items={contacts} />;
 * }
 * ```
 */
export function useExternalContacts() {
  return useExternalContactsQuery();
}

/**
 * Fetch all external contacts INCLUDING soft-deleted ones, for resolving
 * entity refs on old recordings (successors, assignees). Deleted contacts
 * carry a `deletedAt` timestamp so read surfaces can gray them out.
 *
 * Never use this for pickers/selectors — those must only offer live contacts
 * via {@link useExternalContacts}.
 */
export function useExternalContactsForLookup(options?: { enabled?: boolean }) {
  const { token } = useAuthStore();

  return useQuery({
    ...externalContactsForLookupQueryOptions(),
    enabled: (options?.enabled ?? true) && !!token
  });
}

/**
 * Query options for the lookup variant, shared between the hook and route
 * loaders that prefetch the lookup (e.g. the process editor) so both sides
 * stay on the same cache key — and on the same `gcTime`, which only the
 * lookup variant lengthens.
 */
export function externalContactsForLookupQueryOptions() {
  return queryOptions({
    ...externalContactsQueryConfig(true),
    ...LOOKUP_LIST_CACHE
  });
}

function externalContactsQueryConfig(includeDeleted?: boolean) {
  return queryOptions({
    queryKey: externalContactKeys.list(includeDeleted ? { includeDeleted: true } : undefined),
    queryFn: async ({ signal }): Promise<ExternalContact[]> => {
      const { data } = await apiClient.get<ListExternalContactsResponse>(
        includeDeleted
          ? `${listExternalContactsContract.path}?includeDeleted=true`
          : listExternalContactsContract.path,
        { signal }
      );
      return data.data;
    }
  });
}

function useExternalContactsQuery(includeDeleted?: boolean) {
  const { token } = useAuthStore();

  return useQuery({
    ...externalContactsQueryConfig(includeDeleted),
    enabled: !!token
  });
}

export const externalContactQueryOptions = (id: string) =>
  queryOptions({
    queryKey: externalContactKeys.detail(id),
    queryFn: async ({ signal }): Promise<ExternalContact> => {
      // Detail pages must load soft-deleted contacts too (direct URL,
      // links from old recordings); deleted ones carry deletedAt.
      const { data } = await apiClient.get<ShowExternalContactResponse>(
        `${showExternalContactContract.path.replace(':id', id)}?includeDeleted=true`,
        { signal }
      );
      return data.data;
    }
  });

/**
 * Fetch a single external contact by ID
 *
 * @example
 * ```tsx
 * function ExternalContactDetail({ id }: { id: string }) {
 *   const { data: contact, isLoading } = useExternalContact(id);
 *
 *   if (isLoading) return <Skeleton />;
 *   if (!contact) return <NotFound />;
 *
 *   return <DetailView contact={contact} />;
 * }
 * ```
 */
export function useExternalContact(id: string) {
  const { token } = useAuthStore();

  return useQuery({
    ...externalContactQueryOptions(id),
    enabled: !!token && !!id
  });
}
